import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';
import { getCurrentOrgID } from './organization';
import { SERVICE_FLOW_LIMITS } from '@/config/serviceFlows';
import { getFlowSpanOffsets } from '@/utils/serviceFlow';

/**
 * Service Flow data module (spec §5.1, §10).
 *
 * All service-flow reads/writes live here — not tasks.js / events.js.
 *
 * v2 writes go through the dedicated RPC contract (spec §10.2). Templates and
 * the Event attachment are read via PostgREST with RLS effective; the
 * occurrence snapshot is read via `get_service_flow_instance`.
 *
 * Every wrapper THROWS on failure — callers must never mistake a failed fetch
 * for an empty flow (that would publish "empty" over real data). The legacy
 * `save_service_flow_schedule` write path is removed; the server rejects it
 * for v2 organizations anyway (spec §15.1).
 */

const fail = (error, context, message) => {
    ErrorHandler(Error(error?.message || message), { ...context, error }, message);
    throw error;
};

// ---------------------------------------------------------------------------
// Template reads (PostgREST, RLS-filtered)
// ---------------------------------------------------------------------------

/**
 * Paginated template library page. Each row carries itemCount + attached-event
 * count + scheduled span inputs embedded per template. Counts are database
 * aggregates and item sets are bounded by the 200-item template limit.
 *
 * @returns {Promise<{templates: Array, total: number, page: number}>}
 */
const getServiceFlowTemplates = async ({ archived = false, search = '', page = 0 } = {}) => {
    const organizationID = await getCurrentOrgID();
    const pageSize = SERVICE_FLOW_LIMITS.libraryPageSize;

    let query = supabase
        .from('ServiceFlowTemplates')
        .select(`*,
            items:ServiceFlowTemplateItems(count),
            spanItems:ServiceFlowTemplateItems(startOffsetMinutes, durationMinutes),
            attachedEvents:Events!Events_serviceFlowTemplateID_fkey(count),
            attachedLocations:EventServiceFlowLocations!EventServiceFlowLocations_templateID_fkey(count, Event:Events!inner())
        `, { count: 'exact' })
        .eq('organizationID', organizationID)
        .eq('isArchived', archived)
        .eq('attachedEvents.isDeleted', false)
        .eq('attachedLocations.isOverride', true)
        .eq('attachedLocations.Event.isDeleted', false)
        .order('name', { ascending: true })
        .order('id', { ascending: true })
        .range(page * pageSize, (page + 1) * pageSize - 1);

    const trimmed = (search || '').trim();
    if (trimmed) {
        // Server-side filter; '%'/'_' escaped so they can't act as wildcards.
        const safe = trimmed.replace(/[%_\\]/g, c => `\\${c}`);
        query = query.ilike('name', `%${safe}%`);
    }

    const { data, error, count } = await query;
    if (error) fail(error, { query: 'getServiceFlowTemplates', organizationID, archived, search: trimmed, page }, `Something went wrong fetching service flow templates`);

    const templates = data || [];
    return {
        templates: templates.map(({ spanItems, attachedEvents, attachedLocations, ...template }) => ({
            ...template,
            itemCount: template.items?.[0]?.count ?? 0,
            span: getFlowSpanOffsets(spanItems || []),
            attachedEventCount: attachedEvents?.[0]?.count ?? 0,
            attachedLocationCount: (attachedLocations || []).reduce((sum, row) => sum + (row.count ?? 0), 0),
        })),
        total: count ?? templates.length,
        page,
    };
};

/**
 * One template + its items (ordered), with task/team labels resolved for TASK
 * items.
 */
const getServiceFlowTemplate = async (templateID) => {
    if (!templateID) return null;

    const { data, error } = await supabase
        .from('ServiceFlowTemplates')
        .select(`*,
            items:ServiceFlowTemplateItems(*,
                Task:Tasks(id, title, description, dueType),
                VolunteerTeam:VolunteerTeams(id, name)
            )
        `)
        .eq('id', templateID)
        .order('position', { referencedTable: 'ServiceFlowTemplateItems', ascending: true })
        .maybeSingle();

    if (error) fail(error, { query: 'getServiceFlowTemplate', templateID }, `Something went wrong fetching that service flow template`);
    return data || null;
};

/**
 * Event attachment summary for the Event form selector / Event detail:
 * `Events.serviceFlowTemplateID` + optimistic bindingVersion + the attached
 * template header (when still visible).
 */
const getEventServiceFlowAttachment = async (eventID) => {
    if (!eventID) return null;

    const { data, error } = await supabase
        .from('Events')
        .select('id, serviceFlowTemplateID, serviceFlowBindingVersion')
        .eq('id', eventID)
        .maybeSingle();

    if (error) fail(error, { query: 'getEventServiceFlowAttachment', eventID }, `Something went wrong fetching the service flow attachment`);
    if (!data) return null;
    const scopes = await getEventServiceFlowLocations(eventID);
    const { data: labels, error: labelError } = scopes.locations?.length ? await supabase.from('Locations').select('id, name').in('id', scopes.locations.map(b => b.locationID)) : { data: [] };
    if (labelError) fail(labelError, { eventID }, 'Could not load Service Flow location names');
    const locations = await Promise.all((scopes.locations || []).map(async binding => ({
        ...binding,
        locationName: labels?.find(l => l.id === binding.locationID)?.name,
        ServiceFlowTemplate: binding.effectiveTemplateID ? await getServiceFlowTemplate(binding.effectiveTemplateID) : null,
    })));

    let ServiceFlowTemplate = null;
    if (data.serviceFlowTemplateID) {
        const { data: tpl, error: tplError } = await supabase
            .from('ServiceFlowTemplates')
            .select('id, name, description, version, isArchived, items:ServiceFlowTemplateItems(count)')
            .eq('id', data.serviceFlowTemplateID)
            .maybeSingle();
        if (tplError) fail(tplError, { query: 'getEventServiceFlowAttachment.template', eventID }, `Something went wrong fetching the service flow attachment`);
        ServiceFlowTemplate = tpl || null;
    }

    return {
        eventID: data.id,
        serviceFlowTemplateID: data.serviceFlowTemplateID ?? null,
        serviceFlowBindingVersion: data.serviceFlowBindingVersion ?? 0,
        ServiceFlowTemplate,
        locations,
    };
};

// ---------------------------------------------------------------------------
// Template mutations (RPC contract §10.2)
// ---------------------------------------------------------------------------

/**
 * Full-replacement template publish. expectedVersion=0 creates (p_template_id
 * is then the client-generated UUID). Stable item IDs are preserved by the
 * caller so materialized snapshots keep their templateItemID provenance.
 */
const saveServiceFlowTemplate = async ({ templateID, expectedVersion, name, description, items }) => {
    const { data, error } = await supabase.rpc('save_service_flow_template', {
        p_template_id: templateID,
        p_expected_version: expectedVersion ?? 0,
        p_name: name,
        p_description: description ?? null,
        p_items: items ?? [],
    });

    if (error) fail(error, { mutation: 'saveServiceFlowTemplate', input: { templateID, expectedVersion, name } }, `Something went wrong saving the service flow template`);
    return data;
};

/**
 * Archive/restore a template (no snapshot refresh). Archived templates stay
 * attached to existing Events but can't be newly selected.
 */
const setServiceFlowTemplateArchived = async ({ templateID, expectedVersion, archived }) => {
    const { data, error } = await supabase.rpc('set_service_flow_template_archived', {
        p_template_id: templateID,
        p_expected_version: expectedVersion,
        p_archived: !!archived,
    });

    if (error) fail(error, { mutation: 'setServiceFlowTemplateArchived', input: { templateID, expectedVersion, archived } }, `Something went wrong updating the service flow template`);
    return data;
};

/**
 * Change an Event's template attachment. p_template_id NULL detaches — future
 * generation stops, existing occurrence flows are not erased.
 * Returns { eventID, serviceFlowTemplateID, bindingVersion, propagation }.
 */
const setEventServiceFlowTemplate = async ({ eventID, templateID, expectedBindingVersion, expectedTemplateVersion = null, locationID = null, inherit = false }) => {
    const { data, error } = await supabase.rpc('set_event_service_flow_template', {
        p_event_id: eventID,
        p_template_id: templateID ?? null,
        p_expected_binding_version: expectedBindingVersion ?? 0,
        p_expected_template_version: expectedTemplateVersion ?? null,
        p_location_id: locationID,
        p_inherit: inherit,
    });

    if (error) fail(error, { mutation: 'setEventServiceFlowTemplate', input: { eventID, templateID, expectedBindingVersion } }, `Something went wrong updating the service flow template`);
    return data;
};

// ---------------------------------------------------------------------------
// Occurrence snapshot read + per-item mutations (RPC contract §10.2/§10.3)
// ---------------------------------------------------------------------------

/**
 * Authorized occurrence snapshot: revision, protection/customization metadata,
 * typed items with resolvedStartTime, and shared taskContext for TaskCards.
 * RLS remains effective — hidden parents raise not-found/denied, never an
 * apparently-legitimate empty snapshot.
 */
const getServiceFlowInstance = async (eventInstanceID, locationID = null) => {
    if (!eventInstanceID) return null;

    const { data, error } = await supabase.rpc('get_service_flow_instance', {
        p_event_instance_id: eventInstanceID,
        p_location_id: locationID,
    });

    if (error) fail(error, { query: 'getServiceFlowInstance', eventInstanceID }, `Something went wrong fetching the service flow for this date`);
    return data;
};

/**
 * Create/update ONE occurrence item. The server resolves taskInstanceID
 * internally and returns authoritative rows + revision — a saved task link
 * never depends on a second client write.
 */
const saveServiceFlowInstanceItem = async ({ eventInstanceID, expectedRevision, item, locationID = null }) => {
    const { data, error } = await supabase.rpc('save_service_flow_instance_item', {
        p_event_instance_id: eventInstanceID,
        p_expected_revision: expectedRevision,
        p_item: item,
        p_location_id: locationID,
    });

    if (error) fail(error, { mutation: 'saveServiceFlowInstanceItem', input: { eventInstanceID, expectedRevision, itemID: item?.id } }, `Something went wrong saving that service flow item`);
    return data;
};

/**
 * Remove an allowed unused item. Used/shared task instances are preserved by
 * the server (SERVICE_FLOW_TASK_IN_USE).
 */
const removeServiceFlowInstanceItem = async ({ eventInstanceID, expectedRevision, itemID, locationID = null }) => {
    const { data, error } = await supabase.rpc('remove_service_flow_instance_item', {
        p_event_instance_id: eventInstanceID,
        p_expected_revision: expectedRevision,
        p_item_id: itemID,
        p_location_id: locationID,
    });

    if (error) fail(error, { mutation: 'removeServiceFlowInstanceItem', input: { eventInstanceID, expectedRevision, itemID } }, `Something went wrong removing that service flow item`);
    return data;
};

/**
 * Reorder = exact ID permutation; updates positions only, never retimes.
 * Every current row ID must be sent exactly once.
 */
const reorderServiceFlowInstanceItems = async ({ eventInstanceID, expectedRevision, itemIDs, locationID = null }) => {
    const { data, error } = await supabase.rpc('reorder_service_flow_instance_items', {
        p_event_instance_id: eventInstanceID,
        p_expected_revision: expectedRevision,
        p_item_ids: itemIDs,
        p_location_id: locationID,
    });

    if (error) fail(error, { mutation: 'reorderServiceFlowInstanceItems', input: { eventInstanceID, expectedRevision, count: itemIDs?.length } }, `Something went wrong reordering the service flow`);
    return data;
};

/**
 * Explicit guarded reset to the Event's CURRENT attachment (not necessarily
 * this date's old source). Protected/assignment-history dates are rejected
 * server-side.
 */
const resetInstanceServiceFlow = async ({ eventInstanceID, expectedRevision, expectedTemplateVersion, locationID = null }) => {
    const { data, error } = await supabase.rpc('reset_instance_service_flow', {
        p_event_instance_id: eventInstanceID,
        p_expected_revision: expectedRevision,
        p_expected_template_version: expectedTemplateVersion,
        p_location_id: locationID,
    });

    if (error) fail(error, { mutation: 'resetInstanceServiceFlow', input: { eventInstanceID, expectedRevision, expectedTemplateVersion } }, `Something went wrong applying the latest template`);
    return data;
};

// ---------------------------------------------------------------------------
// Legacy (v1) read — kept for the read-only pre-cutover experience only.
// No v1 WRITE path remains: the removed builders must not come back.
// ---------------------------------------------------------------------------

/**
 * Legacy ServiceFlowInstances rows for an EventInstance. Still useful while an
 * organization is on schemaVersion 1 — rendered READ-ONLY next to the
 * upgrading message. Throws on failure (an empty array is a real empty flow,
 * never a fetch failure).
 */
const getServiceFlowForInstance = async (eventInstanceID) => {
    if (!eventInstanceID) return [];

    const { data, error } = await supabase
        .from('ServiceFlowInstances')
        .select(`*,
            TaskInstance:TaskInstances(*,
                Task:Tasks(*),
                VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID)),
                Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))
            )
        `)
        .eq('eventInstanceID', eventInstanceID)
        .order('position', { ascending: true });

    if (error) {
        fail(error, { query: 'getServiceFlowForInstance', eventInstanceID }, `Something went wrong fetching the service flow for this instance`);
    }

    return (data || []).map(item => {
        if (item.TaskInstance?.Assignees) {
            item.TaskInstance.Assignees = item.TaskInstance.Assignees.map(a => ({ ...a, ...a.Person }));
        }
        return item;
    });
};

export const getEventServiceFlowLocations = async (eventID) => {
    const { data, error } = await supabase.rpc('get_event_service_flow_locations', { p_event_id: eventID });
    if (error) fail(error, { eventID }, 'Could not load location service flow templates');
    return data;
};

/** Finalize an occurrence only after all of its location writes have finished. */
export const initializeServiceFlowScopes = async (eventInstanceID) => {
    const { error } = await supabase.rpc('initialize_service_flow_scopes', { p_event_instance_id: eventInstanceID });
    if (error) fail(error, { mutation: 'initializeServiceFlowScopes', eventInstanceID }, 'Could not finish setting up this date\'s service flows');
};

// Compare identities for idempotent retries; never replace the editor's concurrency token.
export const saveEventServiceFlowLocations = async (eventID, bindings = []) => {
    if (!bindings.length) return;
    const current = await getEventServiceFlowLocations(eventID);
    const saved = bindings.map(b => ({ ...b }));
    try {
        for (const binding of saved) {
            const draft = bindings.find(b => b.locationID === binding.locationID);
            const existing = current.locations?.find(b => b.locationID === binding.locationID);
            if (!binding.explicit && !!existing?.isOverride === !!binding.isOverride && (existing?.templateID ?? null) === (binding.templateID ?? null)) { binding.bindingVersion = existing?.bindingVersion ?? 0; if (draft) draft.bindingVersion = binding.bindingVersion; continue; }
            const result = await setEventServiceFlowTemplate({
                eventID, locationID: binding.locationID, templateID: binding.templateID,
                inherit: !binding.isOverride, expectedBindingVersion: binding.bindingVersion ?? 0,
                expectedTemplateVersion: binding.templateVersion ?? null,
            });
            binding.bindingVersion = result.bindingVersion;
            if (draft) { draft.bindingVersion = result.bindingVersion; delete draft.explicit; }
        }
    } catch (error) {
        error.serviceFlowLocations = saved;
        error.partialSave = true;
        throw error;
    }
    return saved;
};

export {
    getServiceFlowTemplates,
    getServiceFlowTemplate,
    getEventServiceFlowAttachment,
    saveServiceFlowTemplate,
    setServiceFlowTemplateArchived,
    setEventServiceFlowTemplate,
    getServiceFlowInstance,
    saveServiceFlowInstanceItem,
    removeServiceFlowInstanceItem,
    reorderServiceFlowInstanceItems,
    resetInstanceServiceFlow,
    getServiceFlowForInstance,
};
